// 외주 의뢰서 통합 페이지 (2026-10-13 대표 "전부 바로 진행" — 계획 docs/계획_외주의뢰서확장_2026-10-13.md §1-2, 화면 정본 미리보기 v11)
// 탭: 새 의뢰 · 보낸 의뢰 · 받은 의뢰(외주 업체로 승인된 곳만) · 연결 요청(업체만) · 월별 요약 · CAD 디자인(기존 화면 그대로)
// 머리 오른쪽 [외주 업체 찾기](웹 전용). CAD 디자인은 기존 designOutsourceRequests 흐름 — 보낸 의뢰 목록에는 함께 보인다.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { Zap, Search, Building2 } from 'lucide-react';
import { db } from '../../firebase/config';
import { getFeatures, isJigLocalOn, isJigWebOn } from '../../utils/features';
import PageHeader from '../common/PageHeader';
import {
    listSentMf, listReceivedMf, listApprovedVendors, getVendorProfile, listMyLinksAsLab, listMyLinksAsVendor,
} from '../../services/outsourceMfApi';
import OutsourceSend from '../OutsourceSend';   // 「새 의뢰」 = PC 케이스에서 보내기(10-30 대표)
import MfCompose from './MfCompose';           // PC 기능이 없는 기공소용 — STL 직접 올리기(지그 없음)
import { MfSentList, MfSentDetail, MfReceivedList } from './MfLists';
import MfMonthly from './MfMonthly';
import { MfVendorFinder, MfLinkRequests } from './MfVendors';
import { S } from './mfUi';

const TabBtn = ({ on, onClick, children, badge }) => (
    <button type="button" onClick={onClick} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', color: on ? '#4f46e5' : '#475569', fontWeight: on ? 700 : 600, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {children}{badge ? <span style={{ background: '#6366f1', color: '#fff', borderRadius: 9, fontSize: 11, padding: '0 6px', minWidth: 18, textAlign: 'center' }}>{badge}</span> : null}
    </button>
);

export default function OutsourceMfHub({ user }) {
    const navigate = useNavigate();
    const companyId = user?.companyId || user?.uid;
    const me = { companyId, uid: user?.uid, name: user?.businessName || user?.companyName || user?.name || '', isOwner: !user?.companyId };
    const [features, setFeatures] = useState(null);
    const [tab, setTab] = useState(null);
    const [finder, setFinder] = useState(false);
    const [fileMode, setFileMode] = useState(false);   // PC 켜진 기공소가 [PC 없이 파일로 보내기]를 고름
    const [detail, setDetail] = useState(null);
    const [sent, setSent] = useState([]);
    const [received, setReceived] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [labLinks, setLabLinks] = useState([]);
    const [vendorLinks, setVendorLinks] = useState([]);
    const [myProfile, setMyProfile] = useState(null);
    const [delegationOk, setDelegationOk] = useState(null);
    const [loadErr, setLoadErr] = useState('');

    const isVendor = myProfile && myProfile.status === 'approved';
    const isCncCompany = user?.businessType === 'outsource';

    const load = useCallback(async () => {
        if (!companyId) return;
        setLoadErr('');
        const safe = (p, label) => p.catch((e) => { console.error('[외주 의뢰서] %s 읽기 실패 code=%s', label, e && e.code); setLoadErr('일부 목록을 불러오지 못했습니다. 새로고침해 주세요.'); return null; });
        const [s, r, v, ll, vl, prof] = await Promise.all([
            safe(listSentMf(companyId), '보낸 의뢰'), safe(listReceivedMf(companyId), '받은 의뢰'),
            safe(listApprovedVendors(), '업체'), safe(listMyLinksAsLab(companyId), '연결'), safe(listMyLinksAsVendor(companyId), '받은 연결'),
            safe(getVendorProfile(companyId), '업체 프로필'),
        ]);
        if (s) setSent(s); if (r) setReceived(r); if (v) setVendors(v); if (ll) setLabLinks(ll); if (vl) setVendorLinks(vl);
        setMyProfile(prof || null);
    }, [companyId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        let on = true;
        getFeatures().then(async (f) => {
            if (!on) return;
            setFeatures(f);
            // 기본 탭: 정식 오픈 = 새 의뢰(미리보기 v11), 베타 미리보기(스위치 OFF) = 지금 쓰는 CAD 화면 — 운영 중인 CAD 흐름이 첫 화면에서 바뀌지 않게
            // 10-30 대표: CAD 디자인 외주 삭제 — 옛 CAD 링크(?kind=cad·compose)도 새 의뢰로
            setTab((t) => t || (isCncCompany ? 'received' : 'new'));
            // 약관 v7.1 동의(보낸 기공소 대표 문서) — rules 가 최종 판정, 화면은 안내만
            const versions = Array.isArray(f.outsourceMfDelegationVersions) ? f.outsourceMfDelegationVersions : ['v7.1'];
            try {
                const owner = user?.companyId ? (await getDoc(doc(db, 'users', user.companyId))).data() : user;
                const exempt = Array.isArray(f.outsourceMfConsentExemptIds) && f.outsourceMfConsentExemptIds.includes(companyId);   // 시험 예외(10-30, rules 와 같은 목록)
                if (on) setDelegationOk(exempt || versions.includes(owner?.consents?.delegation?.version));
            } catch (e) {
                console.error('[외주 의뢰서] 약관 동의 확인 실패 code=%s', e && e.code);
                if (on) setDelegationOk(null);
            }
        });
        return () => { on = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const unopened = received.filter((o) => o.status === 'pending').length;
    const pendingLinks = vendorLinks.filter((l) => l.status === 'requested').length;
    const go = (t) => { setTab(t); setDetail(null); setFinder(false); };

    let body = null;
    // 10-30 대표 "외주의뢰서 PC 기준으로 … 기존 외주의뢰는 없애고": 새 의뢰 = PC(덴클 에이전트)가 찾은 케이스를 골라 보냄.
    //   PC 화면은 지그와 같은 기능 스위치를 쓴다(App.js /jig 와 같은 기준). 꺼진 기공소 또는 [PC 없이 파일로 보내기]를 고르면
    //   덴클 프로그램 안내 + STL 직접 올리기(MfCompose — 지그 없음). 대표 10-30 "PC 기능 꺼진 기공소도 외주를 보낼 수 있게".
    //   결제 전(lab_limited)도 PC 보내기 가능 — 에이전트 alpha.117 부터 결제만 멈춘 기공소는 '외주 전용'(외주 STL + 외주 연결 지그, 지그값은 제조사)으로 돈다(대표 10-30).
    const pcOn = !!features && (isJigLocalOn(features, user, user?.email) || isJigWebOn(features, user, user?.email));
    const compose = <MfCompose me={me} vendors={vendors} links={labLinks} delegationOk={delegationOk} previewOnly={!!features && features.outsourceMf !== true} onSent={() => { load(); go('sent'); }} onFindVendors={() => setFinder(true)} />;
    if (finder) body = <MfVendorFinder me={me} vendors={vendors} links={labLinks} onBack={() => setFinder(false)} onLinksChanged={load} />;
    else if (tab === 'new' && pcOn && !fileMode) body = <OutsourceSend user={user} embedded delegationOk={delegationOk} onFindVendors={() => setFinder(true)} onFileMode={() => setFileMode(true)} />;
    else if (tab === 'new') body = (
        <>
            <div style={{ ...S.info, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#eef2ff', borderColor: '#c7d2fe' }}>
                <span style={{ flex: '1 1 320px' }}>{'덴클 프로그램(PC)을 쓰면 케이스를 고르기만 하면 STL이 자동으로 가고, 커스텀은 지그도 무료로 같이 만들어집니다. 지금은 STL 파일을 직접 올려 보냅니다(지그 없음).'}</span>
                {pcOn
                    ? <button type="button" style={S.ghostBtn} onClick={() => setFileMode(false)}>{'PC 케이스에서 보내기'}</button>
                    : <button type="button" style={S.ghostBtn} onClick={() => navigate('/download-agent')}>{'덴클 프로그램 받기'}</button>}
            </div>
            {compose}
        </>
    );
    else if (tab === 'sent' && detail) body = <MfSentDetail order={detail} me={me} onBack={() => setDetail(null)} onChanged={() => { load(); setDetail(null); }} />;
    else if (tab === 'sent') body = <MfSentList orders={sent} onOpen={setDetail} />;
    else if (tab === 'received') body = <MfReceivedList orders={received} vendorProfile={myProfile} me={me} onChanged={(soft) => { if (!soft) load(); else load(); }} />;
    else if (tab === 'links') body = <MfLinkRequests me={me} links={vendorLinks} receivedOrders={received} onChanged={load} />;
    else if (tab === 'monthly') body = <MfMonthly orders={sent} me={me} />;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
            <PageHeader icon={Zap} title="외주 의뢰서" subtitle="PC에서 찾은 케이스를 CNC·메탈 프린트로 보냅니다. STL은 PC가 바로 보내고, 커스텀은 지그도 무료로 같이 만들어집니다."
                actions={<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {/* 대표 10-13 "외주의뢰서에서 업체등록 — 기공소도 등록하게": 대표 계정에 등록 바로가기(기공소 = 기공소 계정 그대로, CNC = 외주업체 가입 계정). 직원은 설정 페이지가 막으므로 숨김 */}
                    {me.isOwner && <button type="button" style={S.ghostBtn} onClick={() => navigate('/settings/outsource-vendor')}><Building2 size={15} />{'외주 수주 설정'}</button>}
                    {!isCncCompany && <button type="button" style={S.ghostBtn} onClick={() => { setFinder(true); setDetail(null); }}><Search size={15} />{'외주 업체 찾기'}</button>}
                </div>}>
                <div style={{ display: 'inline-flex', gap: 4, background: '#eef2f7', borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
                    {!isCncCompany && <TabBtn on={tab === 'new' && !finder} onClick={() => go('new')}>{'새 의뢰'}</TabBtn>}
                    {!isCncCompany && <TabBtn on={tab === 'sent' && !finder} onClick={() => go('sent')} badge={sent.filter((o) => o.status === 'pending' || o.status === 'opened').length}>{'보낸 의뢰'}</TabBtn>}
                    {(isVendor || received.length > 0) && <TabBtn on={tab === 'received' && !finder} onClick={() => go('received')} badge={unopened}>{'받은 의뢰'}</TabBtn>}
                    {isVendor && <TabBtn on={tab === 'links' && !finder} onClick={() => go('links')} badge={pendingLinks}>{'연결 요청'}</TabBtn>}
                    {!isCncCompany && <TabBtn on={tab === 'monthly' && !finder} onClick={() => go('monthly')}>{'월별 요약'}</TabBtn>}
                </div>
            </PageHeader>
            <div style={{ maxWidth: 1320, margin: '0 auto', padding: 20, width: '100%', boxSizing: 'border-box' }}>
                    {/* 대표 10-15 "안내 공지글이 너무 많아": 「새 의뢰」에서는 맨 위에서 빼고, 아직 열리지 않은 종류를 눌렀을 때만 MfCompose 가 그 카드 아래에 한 번 보인다(문구 그대로). 다른 탭은 종전대로 */}
                    {features && features.outsourceMf !== true && tab !== 'new' && (
                        <div style={{ ...S.info, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>{'미리보기 — 외주 의뢰서 확장은 약관 개정(v7.1) 고지 30일 후 열립니다. 그 전에는 의뢰를 보낼 수 없습니다.'}</div>
                    )}
                    {loadErr && <div style={{ ...S.info, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>{loadErr}</div>}
                    {body}
                </div>
        </div>
    );
}
